import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  Injector,
} from '@angular/core';
import { faMinusSquare } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { isEqual } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { SettingsService } from '../../../core/h3ml/dsl/sensor-fusion/view-settings/settings.service';
import { FusionState } from '../../../core/h3ml/state/fusion-state';
import {
  OutputInfo,
  outputInfoStoreToken,
} from '../../../core/h3ml/state/output-info';
import {
  fusionStoreToken,
  TaskWorkspaceStore,
} from '../../../core/h3ml/state/task-workspace-store';
import { FusionManager } from '../../../core/outputs/annotation/annotation-shared/fusion/fusion-manager';
import { InteractionModeService } from '../../../core/outputs/annotation/annotation-shared/utils/interaction-mode.service';
import { settingsStoreToken } from '../../../cross-team-shared/state/setting-state';
import { delayOnMicrotaskQueue } from '../../../shared/misc-utilities/misc-utils';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { PointCloudOutput } from '../../../team-apis/otterdam/outputs/point-cloud-output';
import { contentInjectorToken } from '../annotation-shared/abstract-annotation-output-component';
import { Status } from '../annotation-shared/state/root/annotation-state';
import { PointCloudStore } from './state/point-cloud-store';

@Component({
  selector: 'h3-point-cloud-layout',
  templateUrl: './point-cloud-layout.component.html',
  styleUrls: [
    '../annotation-shared/toolbars/toolbar-grid.scss',
    './point-cloud-layout.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PointCloudLayoutComponent extends DirectiveSuperclass {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readOnly$ = this.taskWorkspaceStore('readOnly').$;
  status$: Observable<Status> = this.store('status').$;
  withSideViews$ = combineLatest([
    this.store('sideViewsIsAllowed').$,
    this.store.getTransientFromActiveMeshLayer$('sideViewsExpanded'),
  ]).pipe(map(([isAllowed, sideViews]) => isAllowed && sideViews));
  hideToolbars$!: Observable<boolean>;

  isThumbnail$: Observable<boolean>;
  isRightPanelExpanded$ = this.fusionStore('isRightPanelExpanded').$;
  isInSplitView$ = this.taskWorkspaceStore('splitViewOutputName').$.pipe(
    map(isDefined),
  );
  isSingleFrameTask$ = inject(FusionManager).areAllSingleFrame$;
  isSelectedOutput$ = this.taskWorkspaceStore('selectedOutputName').$.pipe(
    map(
      (selectedOutputName) =>
        selectedOutputName === this.outputInfoStore.state().output.name,
    ),
  );
  faMinusSquare = faMinusSquare;
  settingStore = inject(settingsStoreToken);
  size$ = this.store('mainDisplaySize').$.pipe(
    map((size) => size && ([size.width, size.height] as Point2D)),
  );

  protected isObjectsEnabled = this.taskWorkspaceStore.isUsingObjects();
  private settingsService = inject(SettingsService);

  constructor(
    @Inject(contentInjectorToken) public contentInjector: Injector,
    public store: PointCloudStore,
    @Inject(outputInfoStoreToken)
    public outputInfoStore: Store<OutputInfo<PointCloudOutput>>,
    private interactionModeService: InteractionModeService,
    @Inject(fusionStoreToken)
    private fusionStore: Store<FusionState>,
    private taskWorkspaceStore: TaskWorkspaceStore,
  ) {
    super();
    this.isThumbnail$ = this.taskWorkspaceStore.isThumbnail$(
      this.outputInfoStore.state().output.name,
    );
    this.hideToolbars$ = combineLatest([
      this.isThumbnail$,
      this.interactionModeService.isInMode$('canvas'),
    ]).pipe(
      map(
        ([thumbnailMode, canvasMode]) =>
          this.isObjectsEnabled || thumbnailMode || canvasMode,
      ),
    );

    // in tests, we use ngDoCheck to update the size, which emits at the wrong
    // moment during change detection
    if (!environment.shouldUseResizeObserver) {
      this.subscribeTo(
        this.size$.pipe(distinctUntilChanged(isEqual), delayOnMicrotaskQueue()),
        () => this.changeDetectorRef.detectChanges(),
      );
    }
  }

  toggleHideGround() {
    this.settingsService.toggleHideGround();
  }
}
